/**
 * © 2025 Malvys Exaucé Dessou — ZonePro. Tous droits réservés.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, CheckCircle2, BarChart3, FileText, MapPin, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import ZoneProLogo from '../components/ZoneProLogo';

const Landing = () => {
  const { user } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const features = [
    {
      icon: BarChart3,
      title: language === 'fr' ? "Analyse de zonage" : "Zoning Analysis",
      description: language === 'fr' 
        ? "Calculs automatisés basés sur les règlements d'urbanisme de Montréal"
        : "Automated calculations based on Montreal urban planning regulations"
    },
    {
      icon: FileText,
      title: language === 'fr' ? "Extraction PDF" : "PDF Extraction",
      description: language === 'fr'
        ? "IA pour extraire les règles de zonage de vos documents réglementaires"
        : "AI to extract zoning rules from your regulatory documents"
    },
    {
      icon: MapPin,
      title: language === 'fr' ? "Données cadastrales" : "Cadastral Data",
      description: language === 'fr'
        ? "Géométrie et dimensions directement du Cadastre du Québec"
        : "Geometry and dimensions directly from Quebec Cadastre"
    }
  ];

  const benefits = language === 'fr' ? [
    "Analyse en 3 scénarios: conservateur, standard, optimisé",
    "Identification automatique des risques et incertitudes",
    "Rapports PDF professionnels pour vos clients",
    "Support pour Rosemont–La Petite-Patrie"
  ] : [
    "Analysis in 3 scenarios: conservative, standard, optimized",
    "Automatic identification of risks and uncertainties",
    "Professional PDF reports for your clients",
    "Support for Rosemont–La Petite-Patrie"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <header className="relative overflow-hidden bg-zonepro-primary">
        {/* Background Pattern */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        
        {/* Background Image Overlay */}
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1770488767269-9200cca087fa?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        
        {/* Nav */}
        <nav className="relative z-10 flex items-center justify-between px-8 py-5 border-b border-white/10">
          <ZoneProLogo variant="white" size="md" />
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors"
              data-testid="language-toggle-landing"
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium">{language === 'fr' ? 'EN' : 'FR'}</span>
            </button>
            
            {user ? (
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-zonepro-accent hover:bg-zonepro-highlight text-white font-medium px-6"
                data-testid="dashboard-btn"
              >
                {t('nav.dashboard')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-white/70 hover:text-white text-sm font-medium transition-colors"
                  data-testid="login-link"
                >
                  {language === 'fr' ? 'Connexion' : 'Log in'}
                </Link>
                <a 
                  href="https://calendly.com/malvysdessou" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button 
                    className="bg-zonepro-accent hover:bg-zonepro-highlight text-white font-medium px-6" 
                    data-testid="demo-btn"
                  >
                    {language === 'fr' ? 'Réserver une démo' : 'Book a Demo'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </>
            )}
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 px-8 py-20 md:py-28 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center md:text-left"
          >
            <div className="flex items-center gap-2 justify-center md:justify-start mb-6">
              <MapPin className="w-4 h-4 text-zonepro-highlight" />
              <p className="text-zonepro-highlight uppercase tracking-widest text-sm font-medium">
                {language === 'fr' ? "Plateforme d'analyse de zonage • Montréal" : "Zoning Analysis Platform • Montreal"}
              </p>
            </div>
            
            <h2 className="font-bold text-4xl md:text-6xl lg:text-7xl text-white leading-tight mb-6">
              {language === 'fr' ? (
                <>Estimez le potentiel<br /><span className="text-zonepro-highlight">de développement</span></>
              ) : (
                <>Estimate the<br /><span className="text-zonepro-highlight">development potential</span></>
              )}
            </h2>
            
            {/* Slogan */}
            <p className="text-xl md:text-2xl text-white/90 font-medium italic mb-4">
              {language === 'fr' ? "Votre terrain parle. On le traduit." : "Your land speaks. We translate it."}
            </p>
            
            <p className="text-white/60 text-lg max-w-2xl mb-10 mx-auto md:mx-0">
              {language === 'fr' 
                ? "Outil professionnel pour les promoteurs immobiliers, investisseurs et consultants en urbanisme. Analysez rapidement ce qui peut être construit sur un terrain à Montréal."
                : "Professional tool for real estate developers, investors and urban planning consultants. Quickly analyze what can be built on a lot in Montreal."
              }
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center md:justify-start">
              <a 
                href="https://calendly.com/malvysdessou" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button 
                  className="bg-zonepro-accent hover:bg-zonepro-highlight text-white text-base px-8 py-6 font-medium shadow-lg shadow-zonepro-accent/30" 
                  data-testid="cta-btn"
                >
                  <BarChart3 className="w-5 h-5 mr-2" />
                  {language === 'fr' ? 'Réserver une démo' : 'Book a Demo'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <Button 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10 text-base px-8 py-6"
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
              >
                {language === 'fr' ? 'En savoir plus' : 'Learn more'}
              </Button>
            </div>
          </motion.div>
        </div>
        
        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 100V60C240 20 480 0 720 0C960 0 1200 20 1440 60V100H0Z" fill="white"/>
          </svg>
        </div>
      </header>

      {/* Features Section */}
      <section id="features" className="bg-white py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-zonepro-accent uppercase tracking-widest text-sm font-medium mb-4">
              {language === 'fr' ? 'Fonctionnalités' : 'Features'}
            </p>
            <h3 className="font-bold text-3xl md:text-4xl text-zonepro-primary">
              {language === 'fr' ? 'Tout ce dont vous avez besoin' : 'Everything you need'}
            </h3>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-zonepro-surface p-8 rounded-lg hover:shadow-lg transition-shadow duration-300 border border-border/50"
                data-testid={`feature-${index}`}
              >
                <div className="w-14 h-14 bg-zonepro-accent/10 rounded-lg flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-zonepro-accent" />
                </div>
                <h4 className="font-bold text-xl text-zonepro-primary mb-3">
                  {feature.title}
                </h4>
                <p className="text-muted-foreground">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-zonepro-surface py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-zonepro-accent uppercase tracking-widest text-sm font-medium mb-4">
                {language === 'fr' ? 'Pourquoi ZonePro' : 'Why ZonePro'}
              </p>
              <h3 className="font-bold text-3xl md:text-4xl text-zonepro-primary mb-8">
                {language === 'fr' ? 'Gagnez du temps sur vos analyses' : 'Save time on your analyses'}
              </h3>
              <ul className="space-y-4">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                    <span className="text-zonepro-text">{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1767290453327-14def235f9b4?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Montréal"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-lg shadow-xl border border-border/50">
                <p className="font-bold text-3xl text-zonepro-accent font-mono">RPP</p>
                <p className="text-sm text-muted-foreground">Rosemont–La Petite-Patrie</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-zonepro-primary py-20">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="font-bold text-3xl md:text-4xl text-white mb-4">
              {language === 'fr' ? 'Prêt à commencer?' : 'Ready to get started?'}
            </h3>
            <p className="text-white/70 text-lg mb-8">
              {language === 'fr' 
                ? "Découvrez comment ZonePro peut transformer vos analyses immobilières."
                : "Discover how ZonePro can transform your real estate analysis."
              }
            </p>
            <a 
              href="https://calendly.com/malvysdessou" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button 
                className="bg-zonepro-accent hover:bg-zonepro-highlight text-white text-base px-8 py-6 font-medium shadow-lg" 
                data-testid="cta-bottom-btn"
              >
                {language === 'fr' ? 'Réserver une démo' : 'Book a Demo'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zonepro-primary border-t border-white/10 py-8">
        <div className="max-w-6xl mx-auto px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <ZoneProLogo variant="white" size="sm" />
            <div className="text-center">
              <p className="text-white/50 text-xs">
                {t('footer.copyright')}
              </p>
            </div>
            <div className="w-[140px] hidden md:block"></div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
